import { useCallback, useEffect, useRef, useState } from "react";
import { Video, Square, RotateCcw, Camera, SwitchCamera } from "lucide-react";
import { MAX_SECONDS, grabFrame } from "../lib/media";

export const Recorder = ({ onRecorded }) => {
  const liveRef = useRef(null);
  const streamRef = useRef(null);
  const recRef = useRef(null);
  const [state, setState] = useState("idle"); // idle | ready | countdown | recording | done
  const [secs, setSecs] = useState(0);
  const [count, setCount] = useState(3);
  const [facing, setFacing] = useState("user");
  const [preview, setPreview] = useState(null);
  const [err, setErr] = useState("");

  const stopStream = () => streamRef.current?.getTracks().forEach((t) => t.stop());
  useEffect(() => () => stopStream(), []);

  const setLive = useCallback((el) => {
    liveRef.current = el;
    if (el && streamRef.current && el.srcObject !== streamRef.current) {
      el.srcObject = streamRef.current;
      el.play().catch(() => {});
    }
  }, []);

  useEffect(() => {
    if (state !== "recording") return;
    const iv = setInterval(() => setSecs((s) => {
      if (s + 1 >= MAX_SECONDS) recRef.current?.stop();
      return s + 1;
    }), 1000);
    return () => clearInterval(iv);
  }, [state]);

  const startCamera = async (mode = facing) => {
    setErr("");
    stopStream();
    try {
      const stream = await navigator.mediaDevices.getUserMedia({ video: { width: { ideal: 1280 }, height: { ideal: 720 }, facingMode: mode }, audio: true });
      streamRef.current = stream;
      setFacing(mode);
      setState("ready");
      if (liveRef.current) { liveRef.current.srcObject = stream; liveRef.current.play().catch(() => {}); }
    } catch {
      setErr("We couldn't open the camera. Ask a grown-up to allow camera access, or upload a video instead.");
    }
  };

  const record = () => {
    const chunks = [];
    const frames = [];
    const startedAt = Date.now();
    const mime = MediaRecorder.isTypeSupported("video/webm") ? "video/webm" : "video/mp4";
    const rec = new MediaRecorder(streamRef.current, { mimeType: mime });
    const timers = [600, 4500, 8800].map((ms) => setTimeout(() => liveRef.current && grabFrame(liveRef.current).then((b) => b && frames.push(b)), ms));
    rec.ondataavailable = (e) => e.data.size && chunks.push(e.data);
    rec.onstop = () => {
      timers.forEach(clearTimeout);
      const blob = new Blob(chunks, { type: mime });
      const duration = Math.min(MAX_SECONDS, (Date.now() - startedAt) / 1000);
      setPreview(URL.createObjectURL(blob));
      onRecorded({ file: new File([blob], `clip.${mime === "video/webm" ? "webm" : "mp4"}`, { type: mime }), duration, frames });
      setState("done");
      stopStream();
    };
    recRef.current = rec;
    setSecs(0);
    rec.start();
    setState("recording");
  };

  const redo = () => { setPreview(null); onRecorded(null); startCamera(); };

  const beginCountdown = () => {
    setCount(3);
    setState("countdown");
    let n = 3;
    const iv = setInterval(() => {
      n -= 1;
      if (n <= 0) { clearInterval(iv); record(); } else setCount(n);
    }, 800);
  };

  const live = state === "ready" || state === "countdown" || state === "recording";
  const left = MAX_SECONDS - secs;

  return (
    <div data-testid="recorder" className="space-y-4">
      <div className={`relative aspect-[3/4] max-h-[62svh] sm:max-h-none sm:aspect-video mx-auto w-full rounded-3xl border-[3px] bg-[#1B1A2E] overflow-hidden grid place-items-center transition-colors ${state === "recording" ? "border-[#FF6B4A] shadow-[0_0_0_4px_#FF6B4A]" : "border-[#1B1A2E]"}`}>
        {state === "idle" && (
          <button data-testid="recorder-start-camera-btn" onClick={() => startCamera()} className="zn-btn bg-[#FFD23F]"><Camera size={18} /> Turn on camera</button>
        )}
        {live && <video ref={setLive} data-testid="recorder-self-view" autoPlay muted playsInline className={`absolute inset-0 w-full h-full object-cover ${facing === "user" ? "-scale-x-100" : ""}`} />}
        {state === "ready" && <span data-testid="recorder-self-hint" className="absolute top-4 left-4 zn-chip !min-h-0 !py-1 bg-white text-xs">That's you! Get ready</span>}
        {state === "ready" && (
          <button data-testid="recorder-flip-btn" onClick={() => startCamera(facing === "user" ? "environment" : "user")} aria-label="Switch camera" className="absolute top-3 right-3 w-11 h-11 grid place-items-center rounded-full bg-white border-[3px] border-[#1B1A2E]"><SwitchCamera size={20} /></button>
        )}
        {state === "countdown" && <span data-testid="recorder-countdown" key={count} className="absolute font-display text-[7rem] font-bold text-white drop-shadow-[4px_4px_0_#1B1A2E] zn-pop">{count}</span>}
        {state === "done" && preview && <video data-testid="recorder-preview" src={preview} controls playsInline autoPlay loop className={`absolute inset-0 w-full h-full object-contain bg-black ${facing === "user" ? "-scale-x-100" : ""}`} />}
        {state === "recording" && (
          <>
            <span data-testid="recorder-timer" className="absolute top-4 left-4 zn-chip !min-h-0 bg-[#FF6B4A] text-white"><span className="w-2.5 h-2.5 rounded-full bg-white animate-pulse" /> REC {left}s</span>
            <div className="absolute bottom-0 inset-x-0 h-2 bg-white/30"><div className="h-full bg-[#FFD23F] transition-[width] duration-1000 ease-linear" style={{ width: `${(secs / MAX_SECONDS) * 100}%` }} /></div>
          </>
        )}
      </div>
      {err && <p data-testid="recorder-error" className="text-[#E8442A] font-semibold text-sm">{err}</p>}
      <div className="flex gap-3 [&>button]:flex-1 sm:[&>button]:flex-none">
        {state === "ready" && <button data-testid="recorder-record-btn" onClick={beginCountdown} className="zn-btn bg-[#FF6B4A] text-white"><Video size={18} /> Start recording</button>}
        {state === "countdown" && <button disabled className="zn-btn bg-white">Get ready…</button>}
        {state === "recording" && <button data-testid="recorder-stop-btn" onClick={() => recRef.current.stop()} className="zn-btn bg-white"><Square size={18} fill="#1B1A2E" /> Stop</button>}
        {state === "done" && <button data-testid="recorder-redo-btn" onClick={redo} className="zn-btn bg-white"><RotateCcw size={18} /> Record again</button>}
      </div>
    </div>
  );
};
